import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, Loader2, RefreshCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageProvider";
import { usePaywall } from "@/contexts/PaywallContext";
import { useNutrition, type MealType } from "@/hooks/useNutrition";
import {
  useNutritionPhotoAnalyze,
  type PhotoAnalysisResult,
} from "@/hooks/useNutritionPhotoAnalyze";

/**
 * Full-screen modal: capture meal photo → Claude Vision analysis → editable
 * preview → save to nutrition_logs with source='photo'.
 *
 * Camera access: <input type="file" accept="image/*" capture="environment">
 * works in both web (file picker) and mobile native (opens camera directly).
 * No @capacitor/camera dependency required — keeps the bundle lean and
 * respects the briefing's "no new deps" constraint.
 *
 * Photo is base64-encoded in-memory only and discarded after analysis.
 * Nothing persists to Storage (privacy-by-default per memory L24).
 */

type Step = "capture" | "preview" | "analyzing" | "result" | "error_limit";

interface PhotoCaptureScreenProps {
  onClose: () => void;
  onLogged?: () => void;
}

const VALID_MEAL_TYPES: MealType[] = ["breakfast", "lunch", "snack", "dinner"];

function isMealType(v: string): v is MealType {
  return (VALID_MEAL_TYPES as string[]).includes(v);
}

export default function PhotoCaptureScreen({ onClose, onLogged }: PhotoCaptureScreenProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { openPaywall } = usePaywall();
  const { logMeal, userId } = useNutrition();
  const saveLock = useRef(false);
  const formOwner = useRef<string | null>(null);
  useEffect(() => {
    if (formOwner.current && formOwner.current !== userId) { onClose(); return; }
    if (userId) formOwner.current = userId;
  }, [userId, onClose]);
  const [saving, setSaving] = useState(false);
  const { analyzePhoto, isAnalyzing } = useNutritionPhotoAnalyze();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<Step>("capture");
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [result, setResult] = useState<PhotoAnalysisResult | null>(null);

  // Editable form state (populated after analysis)
  const [formDescription, setFormDescription] = useState("");
  const [formMealType, setFormMealType] = useState<MealType>("snack");
  const [formKcal, setFormKcal] = useState(0);
  const [formProtein, setFormProtein] = useState(0);
  const [formCarbs, setFormCarbs] = useState(0);
  const [formFat, setFormFat] = useState(0);

  const triggerCameraPick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip "data:image/jpeg;base64," prefix to get raw base64.
      const commaIdx = dataUrl.indexOf(",");
      const raw = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
      setImageDataUrl(dataUrl);
      setImageBase64(raw);
      setStep("preview");
    };
    reader.onerror = () => {
      toast.error(t("nutrition.photo.error"));
      setStep("capture");
    };
    reader.readAsDataURL(file);
  }, [t]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so user can re-pick the same file if they retake.
    if (e.target) e.target.value = "";
  }, [handleFile]);

  const handleAnalyze = useCallback(async () => {
    if (!imageBase64) return;
    setStep("analyzing");
    const { result: analysis, error: analyzeError } = await analyzePhoto(imageBase64);
    if (analyzeError === "free_photo_used") {
      setStep("error_limit");
      return;
    }
    if (analyzeError === "photo_daily_cap") {
      toast.error(t("nutrition.photo.daily_cap"));
      setStep("preview");
      return;
    }
    if (analyzeError || !analysis) {
      toast.error(t("nutrition.photo.error"));
      setStep("preview");
      return;
    }
    setResult(analysis);
    setFormDescription(analysis.description);
    setFormMealType(isMealType(analysis.meal_type_guess) ? analysis.meal_type_guess : "snack");
    setFormKcal(analysis.kcal);
    setFormProtein(analysis.protein);
    setFormCarbs(analysis.carbs);
    setFormFat(analysis.fat);
    setStep("result");
  }, [imageBase64, analyzePhoto, t]);

  const handleRetake = useCallback(() => {
    setImageDataUrl(null);
    setImageBase64(null);
    setResult(null);
    setStep("capture");
  }, []);

  const handleSave = useCallback(async () => {
    if (!result || saveLock.current) return;
    saveLock.current = true; setSaving(true);
    try {
      await logMeal({
        meal_type: formMealType,
        description: formDescription,
        kcal: formKcal,
        protein: formProtein,
        carbs: formCarbs,
        fat: formFat,
        source: "photo",
        // Pass confidence + ingredients via logMeal extras (see logMeal patch).
        confidence: result.confidence,
        ingredients_detected: result.ingredients,
      } as any);
      toast.success(t("nutrition.photo.save"));
      if (onLogged) onLogged();
      onClose();
    } catch (e) {
      console.error("[PhotoCaptureScreen] save failed:", e);
      toast.error(t("nutrition.photo.error"));
    } finally { saveLock.current = false; setSaving(false); }
  }, [result, formMealType, formDescription, formKcal, formProtein, formCarbs, formFat, logMeal, onLogged, onClose, t]);

  // Confidence label + color
  const confidenceLabel = (() => {
    if (!result) return null;
    if (result.confidence >= 0.85) return { text: t("nutrition.photo.confidence_high"), color: "hsl(140 60% 50%)" };
    if (result.confidence >= 0.60) return { text: t("nutrition.photo.confidence_medium"), color: "hsl(38 92% 55%)" };
    return { text: t("nutrition.photo.confidence_low"), color: "hsl(0 72% 60%)" };
  })();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "hsl(220 35% 5%)" }}
    >
      {/* Header — close button */}
      <div className="flex items-center justify-between px-4 pt-6 pb-3 relative z-10">
        <button
          type="button"
          onClick={() => { if (!saveLock.current) onClose(); }} disabled={saving}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 active:scale-90 transition-transform"
          aria-label={t("common.close") || "Close"}
        >
          <X size={18} className="text-white/70" />
        </button>
        {step === "result" && confidenceLabel && (
          <div
            className="px-3 py-1.5 rounded-full font-display text-[10px] font-semibold tracking-wide"
            style={{ background: `${confidenceLabel.color}22`, color: confidenceLabel.color, border: `1px solid ${confidenceLabel.color}55` }}
          >
            {confidenceLabel.text}
          </div>
        )}
      </div>

      {/* Hidden file input — camera capture on mobile, file picker on web */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInputChange}
        className="hidden"
      />

      <AnimatePresence mode="wait">
        {/* ─── STEP 1: CAPTURE ─── */}
        {step === "capture" && (
          <motion.div
            key="capture"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex-1 flex flex-col items-center justify-center px-8"
          >
            <Camera size={80} className="mb-6 text-white/30" strokeWidth={1.5} />
            <p className="text-[14px] font-body text-white/60 text-center mb-8">
              {t("nutrition.photo.empty_state")}
            </p>
            <button
              type="button"
              onClick={triggerCameraPick}
              className="w-full max-w-xs py-4 rounded-2xl font-display text-[15px] font-semibold tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform"
              style={{
                background: "linear-gradient(135deg, hsl(187 72% 48%), hsl(187 72% 38%))",
                color: "white",
                boxShadow: "0 0 24px hsl(187 72% 50% / 0.30)",
              }}
            >
              <Camera size={18} />
              {t("nutrition.photo.capture")}
            </button>
          </motion.div>
        )}

        {/* ─── STEP 2: PREVIEW ─── */}
        {step === "preview" && imageDataUrl && (
          <motion.div
            key="preview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col px-4 pb-6"
          >
            <div className="flex-1 flex items-center justify-center">
              <img
                src={imageDataUrl}
                alt="meal preview"
                className="max-h-[60vh] max-w-full rounded-2xl object-contain"
                style={{ boxShadow: "0 0 30px hsl(187 72% 50% / 0.20)" }}
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleRetake} disabled={saving}
                className="flex-1 py-3 rounded-xl font-display text-[13px] font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                style={{ background: "hsl(220 18% 14%)", color: "white", border: "1px solid hsl(220 18% 22%)" }}
              >
                <RefreshCcw size={14} />
                {t("nutrition.photo.retake")}
              </button>
              <button
                type="button"
                onClick={handleAnalyze}
                className="flex-[2] py-3 rounded-xl font-display text-[13px] font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                style={{
                  background: "linear-gradient(135deg, hsl(187 72% 48%), hsl(187 72% 38%))",
                  color: "white",
                  boxShadow: "0 0 16px hsl(187 72% 50% / 0.30)",
                }}
              >
                <Check size={14} />
                {t("nutrition.photo.analyze")}
              </button>
            </div>
          </motion.div>
        )}

        {/* ─── STEP 3: ANALYZING ─── */}
        {(step === "analyzing" || isAnalyzing) && step !== "result" && (
          <motion.div
            key="analyzing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col items-center justify-center px-8"
          >
            <Loader2 size={56} className="animate-spin mb-6" style={{ color: "hsl(187 72% 55%)" }} />
            <p className="font-display text-[16px] text-white/80 tracking-wide">
              {t("nutrition.photo.analyzing")}
            </p>
          </motion.div>
        )}

        {/* ─── STEP 4: RESULT ─── */}
        {step === "result" && result && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex-1 flex flex-col px-4 pb-6 overflow-y-auto"
          >
            {imageDataUrl && (
              <img
                src={imageDataUrl}
                alt="meal"
                className="w-full max-h-[28vh] rounded-2xl object-cover mb-4"
              />
            )}

            <input
              type="text"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="w-full rounded-xl px-3 py-3 font-display text-[16px] font-semibold text-white outline-none mb-3"
              style={{ background: "hsl(220 18% 12%)", border: "1px solid hsl(220 18% 22%)" }}
            />

            <select
              value={formMealType}
              onChange={(e) => setFormMealType(e.target.value as MealType)}
              className="w-full rounded-xl px-3 py-2.5 font-display text-[13px] text-white outline-none mb-4"
              style={{ background: "hsl(220 18% 12%)", border: "1px solid hsl(220 18% 22%)", colorScheme: "dark" }}
            >
              <option value="breakfast">{t("nutrition.breakfast")}</option>
              <option value="lunch">{t("nutrition.lunch")}</option>
              <option value="snack">{t("nutrition.snack")}</option>
              <option value="dinner">{t("nutrition.dinner")}</option>
            </select>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <NumField label="kcal" value={formKcal} onChange={setFormKcal} />
              <NumField label="P (g)" value={formProtein} onChange={setFormProtein} />
              <NumField label="C (g)" value={formCarbs} onChange={setFormCarbs} />
              <NumField label="F (g)" value={formFat} onChange={setFormFat} />
            </div>

            {result.ingredients.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-display tracking-[0.18em] uppercase mb-2" style={{ color: "hsl(187 72% 60% / 0.6)" }}>
                  {t("nutrition.photo.ingredients_label")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {result.ingredients.map((ing, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full text-[11px] font-body" style={{ background: "hsl(187 72% 50% / 0.10)", color: "hsl(187 72% 70% / 0.85)", border: "1px solid hsl(187 72% 50% / 0.22)" }}>
                      {ing}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-auto pt-4">
              <button
                type="button"
                onClick={handleRetake} disabled={saving}
                className="flex-1 py-3 rounded-xl font-display text-[13px] font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                style={{ background: "hsl(220 18% 14%)", color: "white", border: "1px solid hsl(220 18% 22%)" }}
              >
                <RefreshCcw size={14} />
                {t("nutrition.photo.retake")}
              </button>
              <button
                type="button"
                onClick={handleSave} disabled={saving || !userId}
                className="flex-[2] py-3 rounded-xl font-display text-[14px] font-bold tracking-wide active:scale-95 transition-transform"
                style={{
                  background: "linear-gradient(135deg, hsl(187 72% 48%), hsl(187 72% 38%))",
                  color: "white",
                  boxShadow: "0 0 16px hsl(187 72% 50% / 0.30)",
                }}
              >
                {t("nutrition.photo.save")}
              </button>
            </div>
          </motion.div>
        )}

        {/* ─── ERROR: análise gratuita VITALÍCIA já usada (sem créditos) ─── */}
        {step === "error_limit" && (
          <motion.div
            key="error_limit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col items-center justify-center px-8 text-center"
          >
            <Camera size={64} className="mb-5 text-white/30" strokeWidth={1.5} />
            <p className="font-display text-[17px] font-semibold text-white mb-2">
              {t("nutrition.photo.limit_title")}
            </p>
            <p className="text-[14px] font-body text-white/70 mb-8 leading-relaxed">
              {t("nutrition.photo.upgrade_limit")}
            </p>
            <button
              type="button"
              onClick={() => openPaywall({ variant: "quota_exceeded", quotaKind: "image", placement: "nutrition_photo_lifetime" })}
              className="w-full max-w-xs py-3.5 rounded-xl font-display text-[14px] font-bold tracking-wide active:scale-95 transition-transform mb-3"
              style={{
                background: "linear-gradient(135deg, hsl(187 72% 48%), hsl(187 72% 38%))",
                color: "white",
                boxShadow: "0 0 16px hsl(187 72% 50% / 0.30)",
              }}
            >
              {t("nutrition.photo.cta_premium")}
            </button>
            <button
              type="button"
              onClick={() => { onClose(); navigate("/creditos"); }}
              className="w-full max-w-xs py-3 rounded-xl font-display text-[13px] font-semibold active:scale-95 transition-transform mb-4"
              style={{ background: "hsl(220 18% 14%)", color: "white", border: "1px solid hsl(220 18% 22%)" }}
            >
              {t("nutrition.photo.cta_credits")}
            </button>
            <button
              type="button"
              onClick={() => { if (!saveLock.current) onClose(); }} disabled={saving}
              className="text-[12px] font-body text-white/40 active:scale-95 transition-transform"
            >
              {t("common.close") || "Close"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── inline NumField subcomponent ──────────────────────────────────────
function NumField({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <p className="text-[10px] font-display tracking-wide mb-1" style={{ color: "hsl(218 14% 50%)" }}>{label}</p>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
        className="w-full rounded-xl px-3 py-2 text-[14px] font-display font-semibold text-white outline-none"
        style={{ background: "hsl(220 18% 12%)", border: "1px solid hsl(220 18% 22%)" }}
      />
    </div>
  );
}
